import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkle } from "lucide-react";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "Reviews", id: "reviews" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

export const Navbar = ({ onNavigate, onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-brand-cream/90 shadow-soft"
          : "bg-transparent"
      }`}
    >
      <nav
        className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 py-4"
        aria-label="Primary"
      >
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-2.5 text-left"
          aria-label="Green Star Unisex Salon - go to home"
        >
          <span
            className={`w-12 h-12 rounded-full flex items-center justify-center ring-2 ring-brand-gold/70 transition-colors duration-500 ${
              scrolled ? "bg-brand-green text-brand-goldlight" : "bg-white/15 text-brand-goldlight backdrop-blur-md"
            }`}
          >
            <Sparkle size={22} aria-hidden="true" />
          </span>
          <span>
            <span
              className={`block font-serif text-2xl sm:text-3xl font-bold leading-none transition-colors duration-500 ${
                scrolled ? "text-brand-green" : "text-white"
              }`}
            >
              Green Star
            </span>
            <span
              className={`block text-[11px] tracking-[0.24em] uppercase mt-1 transition-colors duration-500 ${
                scrolled ? "text-brand-mute" : "text-white/70"
              }`}
            >
              Unisex Salon
            </span>
          </span>
        </button>

        <ul className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <li key={l.id}>
              <button
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-brand-gold ${
                  scrolled ? "text-brand-ink" : "text-white/90"
                }`}
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-button"
            onClick={onBook}
            className="hidden sm:inline-flex items-center rounded-full bg-brand-gold px-6 py-2.5 text-sm font-semibold text-white shadow-gold transition-transform duration-300 hover:scale-[1.04] hover:bg-brand-green"
          >
            Book Appointment
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            className={`lg:hidden w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-300 ${
              scrolled ? "text-brand-green" : "text-white"
            }`}
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-brand-cream/95 border-t border-brand-green/10"
          >
            <ul className="px-6 py-6 space-y-1">
              {LINKS.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <button
                    data-testid={`nav-mobile-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="w-full text-left py-3 text-lg font-serif text-brand-ink hover:text-brand-green transition-colors"
                  >
                    {l.label}
                  </button>
                </motion.li>
              ))}
              <li className="pt-4">
                <button
                  data-testid="nav-mobile-book-button"
                  onClick={() => {
                    setOpen(false);
                    onBook();
                  }}
                  className="w-full rounded-full bg-brand-green py-4 text-white font-semibold"
                >
                  Book Appointment
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
